/**
 * Správce a editor vlastních programů (řazení / filtr / hledání).
 * Tři režimy tvorby s konverzí nahoru: Vizuální → Výrazy → JavaScript.
 */
import { EXPR_IDENTIFIERS, LANG_DOC, validateExpression } from '../core/programLang';
import {
  createProgram,
  programJs,
  visualToExpr,
  type CustomProgram,
  type ProgramMode,
  type VisualFilterRow,
} from '../core/programs';
import { JS_DOC, runProgram } from '../core/programRun';
import type { Itinerary } from '../core/types';
import { departMsOf, type Store } from '../state/store';
import { openDialog } from './dialogs';
import { el, clear } from './dom';
import { infoDot } from './help';
import { icon } from './icons';

const KIND_LABEL = { filter: 'Filtr', sort: 'Řazení', search: 'Hledání' } as const;
const MODE_LABEL: Record<ProgramMode, string> = { visual: 'Vizuální', expr: 'Výrazy', js: 'JavaScript' };

export function openProgramManager(store: Store, getItins: () => Itinerary[], onChanged: () => void): void {
  const body = el('div', {});
  const list = el('div', { className: 'profile-list' });
  const rerender = (): void => {
    clear(list);
    const progs = store.get().customPrograms;
    if (progs.length === 0) list.append(el('div', { className: 'muted' }, 'Zatím žádné programy. Vytvořte si vlastní řazení, filtr nebo hledání.'));
    for (const p of progs) {
      list.append(
        el(
          'div',
          { className: 'profile-row' },
          el('span', { className: 'badge' }, KIND_LABEL[p.kind]),
          el('div', { className: 'profile-row__main' }, el('div', { className: 'profile-row__name' }, p.name), el('div', { className: 'profile-row__desc' }, `režim: ${MODE_LABEL[p.mode]}`)),
          el('button', { className: 'btn btn--small', onclick: () => openProgramEditor(store, p.id, getItins, () => { onChanged(); rerender(); }) }, icon('edit', { size: 13 }), ' Upravit'),
          el('button', {
            className: 'btn btn--small btn--ghost btn--icon',
            'aria-label': `Smazat program ${p.name}`,
            onclick: () => {
              if (!window.confirm(`Smazat program „${p.name}"?`)) return;
              const s = store.get();
              store.update({
                customPrograms: s.customPrograms.filter((x) => x.id !== p.id),
                customSortId: s.customSortId === p.id ? null : s.customSortId,
                customSearchId: s.customSearchId === p.id ? null : s.customSearchId,
                customFilterIds: s.customFilterIds.filter((x) => x !== p.id),
              });
              onChanged();
              rerender();
            },
          }, icon('trash', { size: 13 })),
        ),
      );
    }
  };
  rerender();

  body.append(
    el('div', { className: 'muted', style: 'margin-bottom:8px' }, 'Programy běží v sandboxu s timeoutem — chyba ani nekonečný cyklus aplikaci neshodí. Surový JavaScript se nikdy nepřebírá ze sdílených odkazů.'),
    list,
    el(
      'div',
      { className: 'row row--wrap', style: 'margin-top:10px' },
      ...(['sort', 'filter', 'search'] as const).map((kind) =>
        el('button', {
          className: 'btn',
          onclick: () => {
            const p = createProgram(kind);
            store.update({ customPrograms: [...store.get().customPrograms, p] });
            openProgramEditor(store, p.id, getItins, () => { onChanged(); rerender(); });
            rerender();
          },
        }, icon('plus', { size: 13 }), ` Nové ${KIND_LABEL[kind].toLowerCase()}`),
      ),
    ),
  );

  const dlg = openDialog({ title: 'Vlastní programy', body, wide: true, footer: [el('button', { className: 'btn btn--primary', onclick: () => dlg.close() }, 'Hotovo')] });
}

export function openProgramEditor(store: Store, id: string, getItins: () => Itinerary[], onChanged: () => void): void {
  const get = (): CustomProgram | undefined => store.get().customPrograms.find((p) => p.id === id);
  const p0 = get();
  if (!p0) return;
  const patch = (ch: Partial<CustomProgram>): void =>
    store.update({ customPrograms: store.get().customPrograms.map((p) => (p.id === id ? { ...p, ...ch } : p)) });

  const nameInput = el('input', { className: 'input', value: p0.name, 'aria-label': 'Název programu', onchange: (e: Event) => patch({ name: (e.target as HTMLInputElement).value.trim() || p0.name }) });
  const tabs = el('div', { className: 'prog-tabs', role: 'tablist' });
  const editorBox = el('div', {});
  const statusBox = el('div', { role: 'status', style: 'margin-top:8px' });

  const numericFields = EXPR_IDENTIFIERS.filter((i) => !i.ctx && i.type === 'number').map((i) => i.name);
  const boolFields = EXPR_IDENTIFIERS.filter((i) => !i.ctx && i.type === 'boolean').map((i) => i.name);
  const ctxFields = EXPR_IDENTIFIERS.filter((i) => i.ctx).map((i) => i.name);

  const rerender = (): void => {
    const p = get();
    if (!p) return;
    clear(tabs);
    for (const m of ['visual', 'expr', 'js'] as const) {
      tabs.append(
        el('button', {
          className: `prog-tab${p.mode === m ? ' prog-tab--on' : ''}`,
          role: 'tab',
          'aria-selected': String(p.mode === m),
          onclick: () => {
            // Konverze nahoru: vizuální → výrazy → JS; dolů jen informativně.
            const cur = get()!;
            if (m === 'expr' && cur.mode === 'visual') patch({ mode: m, expr: visualToExpr(cur) });
            else if (m === 'js' && cur.mode !== 'js') {
              const out = programJs(cur);
              patch({ mode: m, js: 'js' in out ? out.js : cur.js });
              if ('error' in out) showStatus(`Převod selhal: ${out.error}`, true);
            } else patch({ mode: m });
            rerender();
          },
        }, MODE_LABEL[m]),
      );
    }
    clear(editorBox);
    if (p.mode === 'visual') renderVisual(p);
    else if (p.mode === 'expr') {
      editorBox.append(
        el('textarea', { className: 'prog-code', 'aria-label': 'Výrazový program', onchange: (e: Event) => patch({ expr: (e.target as HTMLTextAreaElement).value }) }, p.expr),
        el('details', {}, el('summary', { className: 'muted', style: 'cursor:pointer' }, 'Referenční dokumentace jazyka'), el('div', { className: 'prog-doc' }, LANG_DOC)),
      );
    } else {
      editorBox.append(
        el('textarea', { className: 'prog-code', 'aria-label': 'JavaScript program', onchange: (e: Event) => patch({ js: (e.target as HTMLTextAreaElement).value }) }, p.js),
        el('details', {}, el('summary', { className: 'muted', style: 'cursor:pointer' }, 'Dokumentace JS API'), el('div', { className: 'prog-doc' }, JS_DOC)),
        el('div', { className: 'hint' }, 'Pozn.: zpětný převod JS → výrazy/vizuální není možný (JS je obecnější) — nižší režimy se převádějí jen směrem nahoru.'),
      );
    }
  };

  const renderVisual = (p: CustomProgram): void => {
    const v = p.visual;
    const fieldSel = (val: string, fields: string[], on: (v: string) => void): HTMLElement =>
      el('select', { className: 'select select--small', onchange: (e: Event) => on((e.target as HTMLSelectElement).value) }, ...fields.map((f) => el('option', { value: f, selected: f === val }, f)));
    const opSel = (val: string, on: (v: string) => void): HTMLElement =>
      el('select', { className: 'select select--small', onchange: (e: Event) => on((e.target as HTMLSelectElement).value) }, ...['<', '<=', '>', '>=', '=', '!='].map((o) => el('option', { value: o, selected: o === val }, o)));
    const setV = (mut: (v: CustomProgram['visual']) => void): void => {
      const cur = get()!;
      const nv = structuredClone(cur.visual);
      mut(nv);
      patch({ visual: nv });
      rerender();
    };

    if (p.kind !== 'sort') {
      editorBox.append(el('div', { className: 'label' }, 'Podmínky filtru (řádky se spojují spojkou)'));
      v.filter.forEach((r, i) => {
        editorBox.append(
          el('div', { className: 'prog-row' },
            fieldSel(r.field, [...numericFields, ...boolFields], (x) => setV((nv) => { nv.filter[i].field = x; })),
            opSel(r.op, (x) => setV((nv) => { nv.filter[i].op = x as VisualFilterRow['op']; })),
            el('input', { className: 'input input--small', value: r.value, 'aria-label': 'Hodnota', onchange: (e: Event) => setV((nv) => { nv.filter[i].value = (e.target as HTMLInputElement).value; }) }),
            el('button', { className: 'btn btn--small btn--ghost btn--icon', 'aria-label': 'Smazat podmínku', onclick: () => setV((nv) => { nv.filter.splice(i, 1); }) }, icon('trash', { size: 12 })),
          ),
        );
      });
      editorBox.append(
        el('div', { className: 'row' },
          el('button', { className: 'btn btn--small', onclick: () => setV((nv) => { nv.filter.push({ field: 'cas', op: '<', value: '60' }); }) }, icon('plus', { size: 12 }), ' Podmínka'),
          el('span', { className: 'muted' }, 'spojka:'),
          el('select', { className: 'select select--small', onchange: (e: Event) => setV((nv) => { nv.join = (e.target as HTMLSelectElement).value as 'A' | 'NEBO'; }) },
            el('option', { value: 'A', selected: v.join === 'A' }, 'A (zároveň)'),
            el('option', { value: 'NEBO', selected: v.join === 'NEBO' }, 'NEBO'),
          ),
        ),
      );
    }
    if (p.kind !== 'filter') {
      editorBox.append(el('div', { className: 'label', style: 'margin-top:8px' }, 'Řadicí klíče (v pořadí důležitosti)'));
      v.sort.forEach((k, i) => {
        editorBox.append(
          el('div', { className: 'prog-row' },
            fieldSel(k.field, numericFields, (x) => setV((nv) => { nv.sort[i].field = x; })),
            el('select', { className: 'select select--small', onchange: (e: Event) => setV((nv) => { nv.sort[i].desc = (e.target as HTMLSelectElement).value === '1'; }) },
              el('option', { value: '0', selected: !k.desc }, 'vzestupně'),
              el('option', { value: '1', selected: k.desc }, 'sestupně'),
            ),
            el('span', {}),
            el('button', { className: 'btn btn--small btn--ghost btn--icon', 'aria-label': 'Smazat klíč', onclick: () => setV((nv) => { nv.sort.splice(i, 1); }) }, icon('trash', { size: 12 })),
          ),
        );
      });
      editorBox.append(el('button', { className: 'btn btn--small', onclick: () => setV((nv) => { nv.sort.push({ field: 'cas', desc: false }); }) }, icon('plus', { size: 12 }), ' Klíč'));
    }
    if (p.kind === 'search') {
      editorBox.append(el('div', { className: 'label', style: 'margin-top:8px' }, 'Platí jen KDYŽ (volitelné — jinak vždy)'));
      const w = v.when;
      editorBox.append(
        el('div', { className: 'prog-row' },
          fieldSel(w?.field ?? 'vikend', ctxFields, (x) => setV((nv) => { nv.when = { field: x, op: nv.when?.op ?? '=', value: nv.when?.value ?? 'true' }; })),
          opSel(w?.op ?? '=', (x) => setV((nv) => { if (nv.when) nv.when.op = x as VisualFilterRow['op']; })),
          el('input', { className: 'input input--small', value: w?.value ?? '', placeholder: 'true / 7 / …', 'aria-label': 'Hodnota podmínky', onchange: (e: Event) => setV((nv) => { nv.when = { field: nv.when?.field ?? 'vikend', op: nv.when?.op ?? '=', value: (e.target as HTMLInputElement).value }; }) }),
          el('button', { className: 'btn btn--small btn--ghost', onclick: () => setV((nv) => { nv.when = null; }) }, 'vždy'),
        ),
      );
    }
    editorBox.append(el('div', { className: 'hint' }, `Náhled ve výrazech: ${visualToExpr(p)}`));
  };

  const showStatus = (text: string, isError: boolean): void => {
    clear(statusBox);
    statusBox.append(el('div', { className: isError ? 'prog-error' : 'muted' }, text));
  };

  const testBtn = el('button', {
    className: 'btn',
    onclick: async () => {
      const p = get()!;
      if (p.mode === 'expr') {
        const val = validateExpression(p.expr, p.kind);
        if (!val.ok) { showStatus(val.error, true); return; }
      }
      const itins = getItins();
      if (itins.length === 0) { showStatus('Nejsou žádné trasy k vyzkoušení — zadejte A a B.', false); return; }
      showStatus('Spouštím na aktuálních trasách…', false);
      const res = await runProgram(p, itins, departMsOf(store.get()));
      if (res.error) showStatus(`Chyba: ${res.error}`, true);
      else {
        const names = (res.ids ?? []).map((x) => itins.find((t) => t.id === x)?.name ?? x);
        showStatus(p.kind === 'filter' ? `Prošlo ${names.length}/${itins.length}: ${names.join(', ') || '(nic)'}` : `Výsledek: ${names.join(' → ') || '(prázdné)'}`, false);
      }
    },
  }, icon('check', { size: 13 }), ' Vyzkoušet na aktuálních trasách');

  rerender();
  const dlg = openDialog({
    title: `Program: ${KIND_LABEL[p0.kind]}`,
    body: el('div', {},
      el('div', { className: 'field-grid' }, el('div', {}, el('label', { className: 'label' }, 'Název'), nameInput)),
      el('div', { className: 'row', style: 'margin:8px 0 0' }, tabs, infoDot(store, 'Vizuální skládačku lze převést do výrazů a výrazy do JavaScriptu (kliknutím na záložku). Obráceně to nejde — JS umí víc, než nižší režimy dokážou popsat.')),
      editorBox,
      el('div', { style: 'margin-top:10px' }, testBtn),
      statusBox,
    ),
    wide: true,
    footer: [el('button', { className: 'btn btn--primary', onclick: () => { onChanged(); dlg.close(); } }, 'Hotovo')],
  });
}
